import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useDeviceId } from '@/src/common/hooks/useDeviceId'
import { useIsUserSignedIn } from '@/src/common/hooks/useIsUserSignedIn'
import { useEffect } from 'react'
import { Api } from '@/src/store'

/** 1 minute */
const pollingInterval = 1000 * 60

export function useSessionKey() {
  const deviceId = useDeviceId()
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- we don't care about being fully logged in with a phone number for session keys
  const { isSignedIn } = useIsUserSignedIn()
  const onError = useLogError()
  const { setSessionKey, sessionKey, previousSessionKey, clear } = useSessionKeyStore()

  const query = Api.endpoints['GET /session'].useQuery(undefined, {
    skip: deviceId instanceof Promise,
    pollingInterval,
    refetchOnMountOrArgChange: true,
  })

  const { refetch, data } = query

  useEffect(() => {
    clear()
    void refetch()
  }, [refetch, isSignedIn, deviceId, clear])

  useEffect(() => {
    if (data?.sessionKey) {
      setSessionKey(data.sessionKey)
    }
  }, [data, setSessionKey])

  useEffect(() => {
    if (query.error) {
      onError(query.error)
    }
  }, [query.error, onError])

  return {
    sessionKey,
    previousSessionKey,
  }
}

export type SessionKey = (typeof Api.endpoints)['GET /session']['Types']['ResultType']['sessionKey']

interface SessionKeyStore {
  sessionKey: SessionKey | undefined
  setSessionKey: (sessionKey: SessionKey) => void
  previousSessionKey: SessionKey | undefined
  clear: () => void
}

export const useSessionKeyStore = create(
  persist<SessionKeyStore>(
    (set) => ({
      sessionKey: undefined,
      previousSessionKey: undefined,
      setSessionKey: (sessionKey) =>
        set((state) => ({ previousSessionKey: state.sessionKey, sessionKey })),
      clear: () => set({ sessionKey: undefined, previousSessionKey: undefined }),
    }),
    {
      name: 'session-key',
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
)
